import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export interface FAQItem {
  q: string;
  a: React.ReactNode;
}

interface PageFAQProps {
  faqs: FAQItem[];
  heading?: React.ReactNode;
  subheading?: string;
  /** When true, skip client-side FAQPage JSON-LD injection (e.g. when baked into static HTML) */
  suppressJsonLd?: boolean;
}

/** Extract plain text from a ReactNode for JSON-LD */
function textOf(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (typeof node === "object" && "props" in node) {
    const { children } = (node as React.ReactElement).props || {};
    return textOf(children);
  }
  return "";
}

const PageFAQ = ({ faqs, heading = "Frequently Asked Questions", subheading = "Common Questions", suppressJsonLd = false }: PageFAQProps) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // Inject FAQPage JSON-LD (skipped when schema is baked into static HTML)
  useEffect(() => {
    if (suppressJsonLd || !faqs || faqs.length === 0) return;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": textOf(faq.a),
        },
      })),
    };
    let el = document.getElementById("faq-jsonld") as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = "faq-jsonld";
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonLd);
    return () => { el?.remove(); };
  }, [faqs]);

  if (!faqs || faqs.length === 0) return null;

  return (
    <section className="bg-muted/30 py-14 md:py-20 lg:py-28 border-t border-border/40" id="faq">
      <div className="max-w-[820px] mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-12"
        >
          <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-muted-foreground">{subheading}</p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1] mt-2">
            {heading}
          </h2>
        </motion.div>

        <div>
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-b border-border"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full bg-transparent border-none py-4 md:py-5 flex justify-between items-center cursor-pointer text-left gap-3 md:gap-4 text-foreground hover:text-primary transition-colors"
              >
                <span className="text-[14px] md:text-base font-medium">{faq.q}</span>
                <span className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0 text-base md:text-lg transition-all duration-300 ${openIdx === i ? "bg-primary text-primary-foreground rotate-45" : "bg-muted text-muted-foreground"}`}>
                  +
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? "max-h-[500px]" : "max-h-0"}`}>
                <p className="pb-5 md:pb-6 text-[13px] md:text-[14.5px] font-light text-muted-foreground leading-relaxed max-w-[680px]">
                  {faq.a}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PageFAQ;
